import { Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { memo, useMemo } from 'react';

import { DateField } from '~/components/DateField';
import { InvestorsTable } from '~/components/InvestorsTable';
import { TextField } from '~/components/TextField';
import { CreateCompanyFormValues } from '~/types/formValues';
import { calculateInvestorTotalFieldCount, calculateRoundSummary } from '~/utils/calculations';

import styles from './styles.module.scss';

interface Props {
  roundIndex: number;
}

const Component: React.FC<Props> = ({ roundIndex }) => {
  const formik = useFormikContext<CreateCompanyFormValues>();

  const calculatedData = useMemo(() => {
    const currentRound = formik.values.form.rounds[roundIndex];

    return calculateRoundSummary(currentRound, formik.values.form.firstFinancingDate);
  }, [formik.values.form.firstFinancingDate, formik.values.form.rounds, roundIndex]);

  const investors = useMemo(() => {
    const currentRound = formik.values.form.rounds[roundIndex];

    const uniqueInvestors: CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number]['investors'] =
      [];
    const investors = currentRound.subrounds.map(subround => subround.investors).flat();
    investors.forEach(investor => {
      const existingInvestorIndex = uniqueInvestors.findIndex(
        uniqueInvestor => uniqueInvestor.id === investor.id,
      );

      if (existingInvestorIndex === -1) {
        uniqueInvestors.push({ ...investor });
      } else {
        if (investor.corporate) {
          uniqueInvestors[existingInvestorIndex].corporate = true;
        }

        if (investor.lead) {
          uniqueInvestors[existingInvestorIndex].lead = true;
        }

        if (investor.new) {
          uniqueInvestors[existingInvestorIndex].new = true;
        }
      }
    });

    return uniqueInvestors;
  }, [formik.values.form.rounds, roundIndex]);

  return (
    <div className={styles.summary}>
      <div className={styles.summary__fields}>
        <TextField
          disabled
          label="Total Employees"
          value={calculatedData.data.totalEmployees}
          variant="outlined"
        />
        <TextField
          disabled
          label="Total Investors"
          value={calculatedData.data.totalInvestors}
          variant="outlined"
        />
        <TextField
          disabled
          label="Total New Investors"
          value={calculatedData.data.totalNewInvestors}
          variant="outlined"
        />
        <TextField
          disabled
          label="Total Deal Size"
          value={calculatedData.data.totalDealSize}
          variant="outlined"
        />
        <TextField
          disabled
          label="Share Sold"
          value={calculatedData.data.shareSold}
          variant="outlined"
        />
        <TextField
          disabled
          label="Post Money"
          value={calculatedData.data.postMoney}
          variant="outlined"
        />
        <TextField
          disabled
          error={!!calculatedData.errors.preMoney}
          helperText={calculatedData.errors.preMoney}
          label="Pre Money"
          value={calculatedData.data.preMoney}
          variant="outlined"
        />
        <TextField
          disabled
          label="# of Months from First Financing"
          value={`${calculatedData.data.monthsFromFirstFinancing} months`}
          variant="outlined"
        />
        <TextField
          disabled
          label="# of Months between Announcement and Final Close"
          value={`${calculatedData.data.monthsBetweenAnnouncementAndFinalClose} months`}
          variant="outlined"
        />
        <TextField
          disabled
          label="# of Months between Closings"
          value={`${calculatedData.data.monthsBetweenClosings} months`}
          variant="outlined"
        />
        <DateField
          disabled
          emptyDatePlaceholder="No announcement"
          label="Announced Date"
          value={calculatedData.data.announcedDate}
        />
        <DateField disabled label="Deal Date" value={calculatedData.data.dealDate} />
      </div>
      <div>
        <Typography color="text.primary" fontWeight="700" mb="9px" variant="subtitle1">
          Investors{' '}
          <Typography component="span" variant="subtitle1">
            (Total {investors.length})
          </Typography>
        </Typography>
        <InvestorsTable
          data={investors}
          totalCorporate={calculateInvestorTotalFieldCount(investors, 'corporate')}
          totalFollowOn={investors.length - calculateInvestorTotalFieldCount(investors, 'new')}
          totalLeads={calculateInvestorTotalFieldCount(investors, 'lead')}
          totalNew={calculateInvestorTotalFieldCount(investors, 'new')}
        />
      </div>
    </div>
  );
};

export const Summary = memo(Component);
